import { useMemo } from 'react';
import { Box, Typography, Grid, useTheme } from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell, LabelList,
  ReferenceLine, ResponsiveContainer, Tooltip as RTooltip,
} from 'recharts';

import { formatNumber, formatPercent } from '../breakdownBlock/breakdownBlock';
import GrowthTimeline from '../growthTimeline/growthTimeline';

// New clients come in; suspensions and cancellations take clients out. A suspended
// client stops paying maintenance just like a cancelled one, so it counts as a loss.
// Every bar is one client movement — no bar is broken down, on either side.
const IN_COLOR = '#1baf7a';
const SUSPENDED_COLOR = '#eda100';
const OUT_COLOR = '#e34948';
const NEUTRAL_COLOR = '#8a8a82';

const formatCount = (value) => new Intl.NumberFormat('pt-BR').format(value || 0);

const formatShort = (value) => {
  const abs = Math.abs(value);
  if (abs >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `${(value / 1000).toFixed(1)}k`;
  return `${value}`;
};

const signed = (value, format) => `${value > 0 ? '+' : ''}${format(value)}`;

const resultColor = (value) => (value > 0 ? IN_COLOR : value < 0 ? OUT_COLOR : NEUTRAL_COLOR);

// One bar, one number — the movement the bar's name announces.
const BarTooltip = ({ active, payload, label, format }) => {
  const theme = useTheme();
  if (!active || !payload?.length) return null;

  return (
    <Box sx={{ p: 1, borderRadius: 1.5, background: theme.palette.background.paper, boxShadow: 3 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 'bold' }}>{label}</Typography>
      <Typography sx={{ fontSize: 12 }}>{format(payload[0].value)}</Typography>
    </Box>
  );
};

// One of the two readings (clients / maintenance). Every movement is drawn upwards —
// the bar height is how much it moved; only the result bar can go below zero.
const ComparisonChart = ({ title, growth, churn, data, format, axisFormat }) => {
  const theme = useTheme();
  return (
    <Box>
      <Typography variant="subtitle2" align="center" fontWeight="bold">{title}</Typography>
      <Typography
        variant="caption"
        align="center"
        display="block"
        sx={{ color: resultColor(growth.value), fontWeight: 600 }}
      >
        {/* "=" and not a separator: the movement IS that percentage of the base */}
        {signed(growth.value, format)}
        {growth.percent == null ? '' : ` = ${signed(growth.percent, formatPercent)}`}
        <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
          {' '}sobre {growth.baseLabel}
        </Box>
      </Typography>
      {/* The loss on its own: growth can be positive while churn climbs */}
      <Typography variant="caption" align="center" display="block" sx={{ mb: 1, color: 'text.secondary' }}>
        {churn.label}:{' '}
        <Box component="span" sx={{ color: OUT_COLOR, fontWeight: 600 }}>
          {format(churn.value)}
          {churn.percent == null ? '' : ` = ${formatPercent(churn.percent)}`}
        </Box>
        {' '}da base
      </Typography>
      <Box sx={{ height: 320 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 28, right: 12, left: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.15} />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis
              tickFormatter={axisFormat}
              domain={[
                (dataMin) => Math.min(0, Math.floor(dataMin * 1.2)),
                (dataMax) => Math.ceil(Math.max(0, dataMax) * 1.15),
              ]}
            />
            <RTooltip cursor={false} content={<BarTooltip format={format} />} />
            <ReferenceLine y={0} stroke={theme.palette.text.secondary} strokeOpacity={0.5} />
            <Bar dataKey="value" name="Valor" radius={[6, 6, 0, 0]} barSize={54} isAnimationActive={false}>
              {data.map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
              <LabelList
                dataKey="value"
                position="top"
                formatter={format}
                style={{ fontSize: 12, fontWeight: 'bold' }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default function GrowthComparison({ growth }) {
  const { clientData, maintenanceData } = useMemo(() => {
    const newClients = growth?.newClients || { count: 0, maintenance: 0 };
    const suspended = growth?.suspended || { count: 0, maintenance: 0 };
    const canceled = growth?.canceled || { count: 0, maintenance: 0 };
    const result = growth?.result || { count: 0, maintenance: 0 };

    const bar = (name, value, color) => ({ name, value, color });

    return {
      clientData: [
        bar('Clientes novos', newClients.count, IN_COLOR),
        bar('Suspensos', suspended.count, SUSPENDED_COLOR),
        bar('Cancelados', canceled.count, OUT_COLOR),
        bar('Resultado', result.count, resultColor(result.count)),
      ],
      maintenanceData: [
        bar('Clientes novos', newClients.maintenance, IN_COLOR),
        bar('Suspensos', suspended.maintenance, SUSPENDED_COLOR),
        bar('Cancelados', canceled.maintenance, OUT_COLOR),
        bar('Resultado', result.maintenance, resultColor(result.maintenance)),
      ],
    };
  }, [growth]);

  if (!growth) return null;

  const opening = growth.openingBase || { count: 0, maintenance: 0 };
  const result = growth.result || { count: 0, maintenance: 0 };
  const churn = growth.churn || { count: 0, maintenance: 0 };
  const churnClientsLabel = churn.clientsPercent == null ? '—' : formatPercent(churn.clientsPercent);
  const churnRevenueLabel = churn.revenuePercent == null ? '—' : formatPercent(churn.revenuePercent);

  // A sale with maintenance that has no client behind it cannot be counted as a
  // client, and staying quiet about it would just show a smaller bar. Normally zero.
  const unmatched = growth.unmatchedSales || { count: 0, maintenance: 0 };
  const unmatchedWarning = unmatched.count
    ? ` Atenção: ${formatCount(unmatched.count)} venda(s) do período, somando
       ${formatNumber(unmatched.maintenance)} de manutenção, não têm cliente vinculado no Atendimento e
       ficaram de fora da contagem — o cadastro desses clientes ainda não veio do Gerente.`
    : '';

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
        Crescimento da Empresa (Clientes novos x Cancelamentos)
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <ComparisonChart
            title="Quantidade de Clientes"
            growth={{
              value: result.count,
              percent: growth.growth?.clientsPercent,
              baseLabel: `${formatCount(opening.count)} clientes`,
            }}
            churn={{
              label: 'Taxa de cancelamento',
              value: growth.churn?.count || 0,
              percent: growth.churn?.clientsPercent,
            }}
            data={clientData}
            format={formatCount}
            axisFormat={formatCount}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ComparisonChart
            title="Valor de Manutenção (R$)"
            growth={{
              value: result.maintenance,
              percent: growth.growth?.revenuePercent,
              baseLabel: formatNumber(opening.maintenance),
            }}
            churn={{
              label: 'Manutenção perdida',
              value: growth.churn?.maintenance || 0,
              percent: growth.churn?.revenuePercent,
            }}
            data={maintenanceData}
            format={formatNumber}
            axisFormat={formatShort}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <GrowthTimeline
          days={growth.daily || []}
          totals={{
            newClients: growth.newClients,
            suspended: growth.suspended,
            canceled: growth.canceled,
            result,
          }}
          footnote={`Os dois lados são contados por cliente, nunca por venda. Um cliente é novo no dia da
            sua primeira venda quitada que gera manutenção mensal — quem já pagava manutenção não volta a
            ser novo por comprar de novo, e quem comprou duas vezes no mês é um cliente só, com as duas
            manutenções somadas. Venda de acessório e venda para cliente de hora técnica não somam ninguém
            na base. Suspensos e cancelados entram no dia em que
            pararam de pagar. O crescimento compara o resultado com a base do início do período:
            ${formatCount(opening.count)} clientes e ${formatNumber(opening.maintenance)}. A taxa de
            cancelamento divide o que saiu, cancelados mais suspensos, por essa mesma base:
            ${formatCount(churn.count)} ÷ ${formatCount(opening.count)} = ${churnClientsLabel}. Em manutenção,
            ${formatNumber(churn.maintenance)} ÷ ${formatNumber(opening.maintenance)} = ${churnRevenueLabel}.${unmatchedWarning}`}
        />
      </Box>
    </Box>
  );
}
